import { cn } from '@/lib/utils';

import { LogomarkIcon, SidebarIcon, UncollapseIcon } from '@/react/components/icons/primitives';
import { CollapseIcon } from '@icons/CollapseIcon';

import { GrainWordmark } from './GrainWordmark';

interface SidebarHeaderProps {
  collapsed: boolean;
  onToggle: () => void;
}

const TOGGLE_BUTTON_BASE =
  'group rounded-lg transition-colors duration-150 font-medium hover:bg-filled-2';
const TOGGLE_ICON = 'w-6 h-6 text-icon-primary';

// Sidebar header: the GRAIN wordmark + a collapse toggle when expanded; the
// compact logomark (which doubles as the expand toggle) when collapsed. The
// toggle icon swaps to a directional arrow on hover.
//
// The swap is pure CSS (group-hover) rather than a React `hovered` state: a JS
// hover flag gets stuck "true" when the button element is replaced mid-hover
// (toggling collapse swaps the expanded/collapsed branch, so mouseleave never
// fires) — and since the island is turbo-permanent it never remounts to reset.
export function SidebarHeader({ collapsed, onToggle }: SidebarHeaderProps) {
  if (collapsed) {
    return (
      <div className="flex items-center justify-between py-4 pt-6 px-4">
        <button
          type="button"
          onClick={onToggle}
          className={cn('p-2 mx-auto', TOGGLE_BUTTON_BASE)}
          aria-label="Expand sidebar"
        >
          <LogomarkIcon className={cn(TOGGLE_ICON, 'group-hover:hidden')} aria-hidden="true" />
          <UncollapseIcon className={cn(TOGGLE_ICON, 'hidden group-hover:block')} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    // pl-8 (24px) left-aligns the wordmark with the section headings and row
    // icons below (12px section padding + 12px row padding), per the Figma frame.
    <div className="flex items-center justify-between py-4 pt-6 pl-8 pr-4">
      <GrainWordmark className="h-8 w-[81px] flex-shrink-0 text-text-primary" />
      <button
        type="button"
        onClick={onToggle}
        className={cn('py-2 px-3', TOGGLE_BUTTON_BASE)}
        aria-label="Collapse sidebar"
      >
        <SidebarIcon className={cn(TOGGLE_ICON, 'group-hover:hidden')} aria-hidden="true" />
        <CollapseIcon className={cn(TOGGLE_ICON, 'hidden group-hover:block')} aria-hidden="true" />
      </button>
    </div>
  );
}
